<script setup lang="ts">
  import { Button, ButtonSize, ButtonType, IconName } from '@travelata-libs/ui-kit';
  import { setBodyOverflow } from '@travelata-libs/utils';
  import { computed, nextTick, ref } from 'vue';
  import type { Resort } from '@/shared/model';
  import ResortsFilterSheet from './ResortsFilterSheet.vue';
  import SerpFilterGroup from './SerpFilterGroup.vue';
  import { SERP_RESORTS_FILTER_TEXTS } from '../config/texts';
  import { getInlineFilterOptions } from '../lib/inlineFilterOptions';
  import { getResortsFilterResorts, RESORTS_FILTER_INLINE_LIMIT, toResortFilterOption } from '../lib/resortsFilter';
  import { useSerp } from '../model/serpContainer';
  import type { SerpFilterGroupId, SerpFilterOptionView, SerpOptionsFilterGroupView } from '../model/types';
  import { useSerpFilters } from '../model/useSerpFilters';

  interface ResortsFilterProps {
    group: SerpOptionsFilterGroupView;
  }

  const props = defineProps<ResortsFilterProps>();

  const { useSearchCriteria } = useSerp();
  const { model } = useSearchCriteria();
  const { searchCriteriaDraft, toggle, clearGroup, setResorts } = useSerpFilters();

  const resorts = computed<Resort[]>(() => getResortsFilterResorts(model.value.destination));

  const selectedIds = computed<number[]>(() => searchCriteriaDraft.value.resorts ?? []);

  const countryName = computed<string>(() =>
    model.value.destination && model.value.destination.kind !== 'none' ? model.value.destination.country.name : '',
  );

  const allResortsLabel = computed<string>(() => SERP_RESORTS_FILTER_TEXTS.allInCountry(countryName.value));

  const options = computed<SerpFilterOptionView[]>(() =>
    resorts.value.map((resort) => ({
      ...toResortFilterOption(resort),
      selected: selectedIds.value.includes(resort.id),
      disabled: false,
    })),
  );

  const pinnedKeys = ref<Set<string>>(new Set(selectedIds.value.map(String)));

  const inlineGroup = computed<SerpOptionsFilterGroupView>(() => ({
    ...props.group,
    resetLabel: allResortsLabel.value,
    options: getInlineFilterOptions(options.value, pinnedKeys.value, RESORTS_FILTER_INLINE_LIMIT),
  }));

  const hasHiddenResorts = computed<boolean>(() => inlineGroup.value.options.length < options.value.length);

  const isSheetOpen = ref<boolean>(false);

  const onSelect = (groupId: SerpFilterGroupId, key: string): void => {
    toggle(groupId, key);
  };

  const onReset = (groupId: SerpFilterGroupId): void => {
    clearGroup(groupId);
  };

  const openSheet = (): void => {
    isSheetOpen.value = true;
  };

  const onSheetApply = (resortIds: number[]): void => {
    pinnedKeys.value = new Set([...pinnedKeys.value, ...resortIds.map(String)]);
    setResorts(resortIds);
  };

  const closeSheet = async (): Promise<void> => {
    isSheetOpen.value = false;
    await nextTick();
    setBodyOverflow('hidden');
  };
</script>

<template>
  <div
    v-if="resorts.length"
    class="resorts-filter"
    data-testid="serp-resorts-filter"
  >
    <SerpFilterGroup
      :group="inlineGroup"
      @select="onSelect"
      @reset="onReset"
    />

    <Button
      v-if="hasHiddenResorts"
      class="resorts-filter__show-all"
      :type="ButtonType.Link"
      :size="ButtonSize.L"
      :icon-right="IconName.ChevronDown"
      data-testid="serp-resorts-filter-show-all"
      @click="openSheet"
    >
      {{ SERP_RESORTS_FILTER_TEXTS.showAll }}
    </Button>

    <ResortsFilterSheet
      v-if="isSheetOpen"
      :resorts="resorts"
      :selected-ids="selectedIds"
      :all-label="allResortsLabel"
      @apply="onSheetApply"
      @close="closeSheet"
    />
  </div>
</template>

<style scoped lang="scss">
  .resorts-filter {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);

    &__show-all {
      align-self: flex-start;
    }
  }
</style>
